import React, { useEffect, useState } from 'react';
import { useMsal } from '@azure/msal-react'; 
import { acquireTokenSafe } from '../App';
import { RefreshCw, Download, ExternalLink, AlertTriangle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react';
import { Bar, Pie } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement } from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ArcElement);

// Ken-inspired chart colors
const chartColors = {
    blue: '#58a6ff',
    green: '#3fb950',
    yellow: '#d29922',
    red: '#f85149',
    purple: '#a371f7',
    orange: '#f0883e',
    cyan: '#39d4da',
    gray: '#8b949e',
    text: '#e6edf3',
    textMuted: '#8b949e',
    grid: '#30363d',
    bg: '#161b22',
    border: '#30363d'
};

interface Task {
    TaskID: string;
    Title: string;
    DueDate: string;
    UpdatedDate: string;
    ClientID: string;
    ClientTitle: string;
    AssignedTo: string;
    Status: string;
    IsOverdue: boolean;
}

interface Client {
    ClientID: string;
    Title: string;
    ClientState: string;
}

const Delivery = () => {
    const { instance } = useMsal();
    const [tasks, setTasks] = useState<Task[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [error, setError] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [showAllUsers, setShowAllUsers] = useState(false);
    const [filterClientSystem, setFilterClientSystem] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedAssignee, setSelectedAssignee] = useState<string>('');
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);
    const [showAllOverdueTitles, setShowAllOverdueTitles] = useState(false);
    const [showAllUncompletableTitles, setShowAllUncompletableTitles] = useState(false);

    const fetchClientsAndTasks = async () => {
        setLoading(true);
        try {
            const accounts = instance.getAllAccounts();
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) { setLoading(false); return; }
            const authHeader = `Bearer ${token}`;

            const clientsResponse = await fetch('http://localhost:5001/api/clients', {
                method: 'GET',
                headers: {
                    'Authorization': authHeader,
                    'Content-Type': 'application/json'
                }
            });
            if (!clientsResponse.ok) throw new Error(`HTTP error fetching clients! status: ${clientsResponse.status}`);
            const clientsData = await clientsResponse.json();
            const clientMap = clientsData.clients.map((client: any) => ({
                ClientID: client.ClientID,
                Title: client.Title,
                ClientState: client.ClientState || 'Unknown' // Added ClientState, default to 'Unknown' if missing
            }));
            setClients(clientMap);

            const tasksResponse = await fetch('http://localhost:5001/api/tasks', {
                method: 'GET',
                headers: {
                    'Authorization': authHeader,
                    'Content-Type': 'application/json'
                }
            });
            if (!tasksResponse.ok) throw new Error(`HTTP error fetching tasks! status: ${tasksResponse.status}`);
            const tasksData = await tasksResponse.json();

            const enrichedTasks = tasksData.tasks.map((task: any) => {
                const client = clientMap.find((c: Client) => c.ClientID === task.ClientID);
                return {
                    TaskID: task.TaskID,
                    Title: task.Title,
                    DueDate: task.DueDate,
                    UpdatedDate: task.UpdatedDate,
                    ClientID: task.ClientID,
                    ClientTitle: client ? client.Title : task.ClientID,
                    AssignedTo: task.Assignee,
                    Status: task.Status || '',
                    IsOverdue: task.DueDate && new Date(task.DueDate) < new Date()
                };
            });

            setTasks(enrichedTasks);
            setError(null);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred');
        } finally {
            setLoading(false);
        }
    };

    const refreshTasks = async () => {
        setLoading(true);
        try {
            const accounts = instance.getAllAccounts();
            const token = await acquireTokenSafe(instance, accounts);
            if (!token) { setLoading(false); return; }
            const authHeader = `Bearer ${token}`;

            const clearResponse = await fetch('http://localhost:5001/api/clear-cache', {
                method: 'POST',
                headers: {
                    'Authorization': authHeader,
                    'Content-Type': 'application/json'
                }
            });
            if (!clearResponse.ok) throw new Error('Failed to clear cache');

            await fetchClientsAndTasks();
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An unknown error occurred');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClientsAndTasks();
    }, [instance]);

    // Filter tasks for DataTable
    const filteredTableTasks = tasks.filter(task =>
        Object.values(task).some(value =>
            value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
        ) && (!selectedAssignee || task.AssignedTo === selectedAssignee)
          && (!selectedStatus || task.Status === selectedStatus)
    );

    const uniqueAssignees = Array.from(new Set(tasks.map(task => task.AssignedTo))).sort();
    const uniqueStatuses = Array.from(new Set(tasks.map(task => task.Status))).sort();

    // Bar chart data (Assignees)
    const filteredChartTasks = filterClientSystem
        ? tasks.filter(task => !['Client', 'System'].includes(task.AssignedTo))
        : tasks;

    const assigneeCounts = filteredChartTasks.reduce((acc: { [key: string]: number }, task) => {
        acc[task.AssignedTo] = (acc[task.AssignedTo] || 0) + 1;
        return acc;
    }, {});

    const sortedAssignees = Object.entries(assigneeCounts)
        .sort(([, countA], [, countB]) => countB - countA);

    const displayedAssignees = showAllUsers ? sortedAssignees : sortedAssignees.slice(0, 30);

    const assigneeChartData = {
        labels: displayedAssignees.map(([assignee]) => assignee),
        datasets: [{
            label: 'Tasks Assigned',
            data: displayedAssignees.map(([, count]) => count),
            backgroundColor: chartColors.blue,
            borderColor: chartColors.blue,
            borderWidth: 1
        }]
    };

    const assigneeChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { 
                position: 'top' as const,
                labels: { color: chartColors.text }
            },
            title: { 
                display: true, 
                text: showAllUsers ? 'All Assigned Users' : 'Top 30 Assigned Users',
                color: chartColors.text
            }
        },
        scales: {
            x: {
                title: { display: true, text: 'Assignee', color: chartColors.text },
                ticks: { color: chartColors.textMuted },
                grid: { color: chartColors.grid }
            },
            y: {
                beginAtZero: true,
                title: { display: true, text: 'Number of Tasks', color: chartColors.text },
                ticks: { color: chartColors.textMuted },
                grid: { color: chartColors.grid }
            }
        },
        onClick: (event: any, elements: any[]) => {
            if (elements.length > 0) {
                const index = elements[0].index;
                const assignee = assigneeChartData.labels[index];
                setSelectedAssignee(assignee);
            }
        }
    };

    // Pie chart data for overdue tasks by title
    const overdueTasks = tasks.filter(task => task.IsOverdue);
    const overdueTitleCounts = overdueTasks.reduce((acc: { [key: string]: number }, task) => {
        acc[task.Title] = (acc[task.Title] || 0) + 1;
        return acc;
    }, {});
    const sortedOverdueTitles = Object.entries(overdueTitleCounts).sort(([, countA], [, countB]) => countB - countA);
    const displayedOverdueTitles = showAllOverdueTitles ? sortedOverdueTitles : sortedOverdueTitles.slice(0, 10);

    const overdueTasksChartData = {
        labels: displayedOverdueTitles.map(([title]) => title),
        datasets: [{
            label: 'Overdue Tasks',
            data: displayedOverdueTitles.map(([, count]) => count),
            backgroundColor: [
                chartColors.red,
                chartColors.blue,
                chartColors.yellow,
                chartColors.green,
                chartColors.purple,
                chartColors.orange,
                chartColors.gray,
                chartColors.blue,
                chartColors.purple,
                chartColors.green
            ].slice(0, displayedOverdueTitles.length),
            borderColor: chartColors.border,
            borderWidth: 1
        }]
    };

    const overdueTasksChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { 
                position: 'top' as const,
                labels: { color: chartColors.text }
            },
            title: { 
                display: true, 
                text: showAllOverdueTitles ? 'All Overdue Tasks by Title' : 'Top 10 Overdue Tasks by Title',
                color: chartColors.text
            }
        }
    };

    // Pie chart data for uncompletable tasks by title
    const uncompletableTasks = tasks.filter(task => task.Status.toLowerCase() === 'uncompletable');
    const uncompletableTitleCounts = uncompletableTasks.reduce((acc: { [key: string]: number }, task) => {
        acc[task.Title] = (acc[task.Title] || 0) + 1;
        return acc;
    }, {});
    const sortedUncompletableTitles = Object.entries(uncompletableTitleCounts).sort(([, countA], [, countB]) => countB - countA);
    const displayedUncompletableTitles = showAllUncompletableTitles ? sortedUncompletableTitles : sortedUncompletableTitles.slice(0, 10);

    const uncompletableTasksChartData = {
        labels: displayedUncompletableTitles.map(([title]) => title),
        datasets: [{
            label: 'Uncompletable Tasks',
            data: displayedUncompletableTitles.map(([, count]) => count),
            backgroundColor: [
                chartColors.red,
                chartColors.blue,
                chartColors.yellow,
                chartColors.green,
                chartColors.purple,
                chartColors.orange,
                chartColors.gray,
                chartColors.blue,
                chartColors.purple,
                chartColors.green
            ].slice(0, displayedUncompletableTitles.length),
            borderColor: chartColors.border,
            borderWidth: 1
        }]
    };

    const uncompletableTasksChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { 
                position: 'top' as const,
                labels: { color: chartColors.text }
            },
            title: { 
                display: true, 
                text: showAllUncompletableTitles ? 'All Uncompletable Tasks by Title' : 'Top 10 Uncompletable Tasks by Title',
                color: chartColors.text
            }
        }
    };

    // Overdue Tasks Aging Bar Chart (only tasks with "overdue" status)
    const overdueStatusTasks = tasks.filter(task => task.Status.toLowerCase() === 'overdue' && task.IsOverdue);
    const overdueAgingBuckets = {
        '0-7 days': 0,
        '8-14 days': 0,
        '15-30 days': 0,
        '30-90 days': 0,
        '90-180 days': 0,
        '180+ days': 0
    };

    overdueStatusTasks.forEach(task => {
        const dueDate = new Date(task.DueDate);
        const currentDate = new Date();
        const daysOverdue = Math.floor((currentDate.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24));
        
        if (daysOverdue <= 7) {
            overdueAgingBuckets['0-7 days']++;
        } else if (daysOverdue <= 14) {
            overdueAgingBuckets['8-14 days']++;
        } else if (daysOverdue <= 30) {
            overdueAgingBuckets['15-30 days']++;
        } else if (daysOverdue <= 90) {
            overdueAgingBuckets['30-90 days']++;
        } else if (daysOverdue <= 180) {
            overdueAgingBuckets['90-180 days']++;
        } else {
            overdueAgingBuckets['180+ days']++;
        }
    });

    const overdueAgingChartData = {
        labels: Object.keys(overdueAgingBuckets),
        datasets: [{
            label: 'Overdue Tasks',
            data: Object.values(overdueAgingBuckets),
            backgroundColor: chartColors.red,
            borderColor: chartColors.red,
            borderWidth: 1
        }]
    };

    const overdueAgingChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { 
                position: 'top' as const,
                labels: { color: chartColors.text }
            },
            title: { 
                display: true, 
                text: 'Overdue Tasks Aging',
                color: chartColors.text
            }
        },
        scales: {
            x: {
                title: { display: true, text: 'Days Overdue', color: chartColors.text },
                ticks: { color: chartColors.textMuted },
                grid: { color: chartColors.grid }
            },
            y: {
                beginAtZero: true,
                title: { display: true, text: 'Number of Tasks', color: chartColors.text },
                ticks: { color: chartColors.textMuted },
                grid: { color: chartColors.grid }
            }
        }
    };

    // Totals for dashboard
    const totalOverdue = overdueTasks.length;
    const totalUncompletable = uncompletableTasks.length;

    // Table column type definition
    type TableColumn = {
        accessorKey?: keyof Task;
        id?: string;
        header: string;
        accessorFn?: (row: Task) => string;
        cell?: (props: { row: { original: Task } }) => React.ReactNode;
    };

    // TanStack table column definitions
    const tableColumns: TableColumn[] = [
        {
            accessorKey: 'TaskID',
            header: 'Task ID',
            cell: ({ row }) => (
                <a
                    href={`https://kinore.senta.co/c/v/${row.original.ClientID}#work`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                >
                    {row.original.TaskID}
                    <ExternalLink size={14} />
                </a>
            )
        },
        { accessorKey: 'Title', header: 'Title' },
        { accessorKey: 'DueDate', header: 'Due Date' },
        { accessorKey: 'UpdatedDate', header: 'Updated Date' },
        {
            accessorKey: 'ClientTitle',
            header: 'Client',
            cell: ({ row }) => (
                <a
                    href={`https://kinore.senta.co/c/v/${row.original.ClientID}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}
                >
                    {row.original.ClientTitle}
                    <ExternalLink size={14} />
                </a>
            )
        },
        {
            id: 'ClientState',
            header: 'Client State',
            accessorFn: (row: Task) => {
                const client = clients.find(c => c.ClientID === row.ClientID);
                return client ? client.ClientState : 'Unknown';
            },
            cell: ({ row }) => {
                const client = clients.find(c => c.ClientID === row.original.ClientID);
                const state = client ? client.ClientState : 'Unknown';
                const isAlertState = state.toLowerCase() === 'former client' || state.toLowerCase() === 'test';
                return (
                    <span className="badge" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                        {state}
                        {isAlertState && <AlertTriangle size={14} style={{ color: 'var(--yellow)' }} />}
                    </span>
                );
            }
        },
        { accessorKey: 'AssignedTo', header: 'Assigned To' },
        { accessorKey: 'Status', header: 'Status' }
    ];

    // Legacy columns for CSV download
    const columns = [
        { name: 'Task ID', selector: (row: Task) => row.TaskID },
        { name: 'Title', selector: (row: Task) => row.Title },
        { name: 'Due Date', selector: (row: Task) => row.DueDate },
        { name: 'Updated Date', selector: (row: Task) => row.UpdatedDate },
        { name: 'Client', selector: (row: Task) => row.ClientTitle },
        {
            name: 'Client State',
            selector: (row: Task) => {
                const client = clients.find(c => c.ClientID === row.ClientID);
                return client ? client.ClientState : 'Unknown';
            }
        },
        { name: 'Assigned To', selector: (row: Task) => row.AssignedTo },
        { name: 'Status', selector: (row: Task) => row.Status }
    ];

    const downloadCSV = () => {
        const headers = columns.map(col => col.name).join(',');
        const rows = filteredTableTasks.map(task =>
            columns.map(col => {
                const value = col.selector(task);
                return `"${(value || '').toString().replace(/"/g, '""')}"`;
            }).join(',')
        ).join('\n');
        const csvContent = `${headers}\n${rows}`;

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'tasks.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    if (error) return <div className="card" style={{ margin: '20px', padding: '20px', color: 'var(--red)' }}>Error: {error}</div>;

    // Calculate pagination
    const totalPages = Math.ceil(filteredTableTasks.length / rowsPerPage);
    const paginatedTasks = filteredTableTasks.slice(
        currentPage * rowsPerPage,
        (currentPage + 1) * rowsPerPage
    );

    return (
        <div className="page">
            <div className="page-header">
                <h1>Delivery - Overdue Tasks</h1>
            </div>

            <div className="grid-3">
                <div className="card">
                    <h2>Overdue Tasks Aging</h2>
                    <div style={{ height: '300px' }}>
                        <Bar data={overdueAgingChartData} options={overdueAgingChartOptions} />
                    </div>
                    <div style={{ marginTop: 'var(--spacing-md)', display: 'grid', gap: 'var(--spacing-md)' }}>
                        <div className="stat-card">
                            <div className="stat-label">Total Overdue Tasks</div>
                            <div className="stat-value" style={{ color: 'var(--red)' }}>{totalOverdue}</div>
                        </div>
                        <div className="stat-card">
                            <div className="stat-label">Total Uncompletable Tasks</div>
                            <div className="stat-value" style={{ color: 'var(--yellow)' }}>{totalUncompletable}</div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <h2>Overdue Tasks by Title</h2>
                    <div style={{ marginBottom: 'var(--spacing-md)' }}>
                        <button
                            className="btn btn-secondary"
                            onClick={() => setShowAllOverdueTitles(!showAllOverdueTitles)}
                            disabled={loading}
                        >
                            {showAllOverdueTitles ? 'Show Top 10 Titles' : 'Show All Titles'}
                        </button>
                    </div>
                    <div style={{ height: '300px' }}>
                        <Pie data={overdueTasksChartData} options={overdueTasksChartOptions} />
                    </div>
                </div>

                <div className="card">
                    <h2>Uncompletable Tasks by Title</h2>
                    <div style={{ marginBottom: 'var(--spacing-md)' }}>
                        <button
                            className="btn btn-secondary"
                            onClick={() => setShowAllUncompletableTitles(!showAllUncompletableTitles)}
                            disabled={loading}
                        >
                            {showAllUncompletableTitles ? 'Show Top 10 Titles' : 'Show All Titles'}
                        </button>
                    </div>
                    <div style={{ height: '300px' }}>
                        <Pie data={uncompletableTasksChartData} options={uncompletableTasksChartOptions} />
                    </div>
                </div>
            </div>

            <div className="card" style={{ marginTop: 'var(--spacing-lg)' }}>
                <h2>Tasks by Assignee</h2>
                <div className="filter-bar" style={{ marginBottom: 'var(--spacing-md)' }}>
                    <button
                        className="btn btn-secondary"
                        onClick={() => setShowAllUsers(!showAllUsers)}
                        disabled={loading}
                    >
                        {showAllUsers ? 'Show Top 30 Users' : 'Show All Users'}
                    </button>
                    <button
                        className="btn btn-secondary"
                        onClick={() => setFilterClientSystem(!filterClientSystem)}
                        disabled={loading}
                    >
                        {filterClientSystem ? 'Show Client/System' : 'Hide Client/System'}
                    </button>
                </div>
                <div style={{ height: '400px' }}>
                    <Bar data={assigneeChartData} options={assigneeChartOptions} />
                </div>
            </div>

            <div className="card" style={{ marginTop: 'var(--spacing-lg)' }}>
                <div className="filter-bar">
                    <input
                        type="text"
                        className="form-input"
                        placeholder="Search Tasks..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ minWidth: '200px' }}
                    />
                    <select
                        className="form-select"
                        value={selectedAssignee}
                        onChange={(e) => setSelectedAssignee(e.target.value)}
                    >
                        <option value="">All Assignees</option>
                        {uniqueAssignees.map(assignee => (
                            <option key={assignee} value={assignee}>{assignee}</option>
                        ))}
                    </select>
                    <select
                        className="form-select"
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value)}
                    >
                        <option value="">All Statuses</option>
                        {uniqueStatuses.map(status => (
                            <option key={status} value={status}>{status || 'None'}</option>
                        ))}
                    </select>
                    <button
                        className="btn btn-primary"
                        onClick={refreshTasks}
                        disabled={loading}
                    >
                        <RefreshCw size={16} />
                        {loading ? 'Refreshing...' : 'Refresh Tasks'}
                    </button>
                </div>

                <table className="data-table">
                    <thead>
                        <tr>
                            {tableColumns.map((col) => (
                                <th key={col.accessorKey || col.id}>{col.header}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {paginatedTasks.map((task) => (
                            <tr key={task.TaskID}>
                                {tableColumns.map((col) => (
                                    <td key={col.accessorKey || col.id}>
                                        {col.cell 
                                            ? col.cell({ row: { original: task } })
                                            : col.accessorFn 
                                                ? col.accessorFn(task)
                                                : (task as any)[col.accessorKey!]
                                        }
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 'var(--spacing-md)', padding: 'var(--spacing-sm) 0' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                        <span style={{ color: 'var(--text-secondary)' }}>Rows per page:</span>
                        <select
                            className="form-select"
                            value={rowsPerPage}
                            onChange={(e) => {
                                setRowsPerPage(Number(e.target.value));
                                setCurrentPage(0);
                            }}
                            style={{ width: 'auto' }}
                        >
                            {[10, 25, 50, 100].map(size => (
                                <option key={size} value={size}>{size}</option>
                            ))}
                        </select>
                        <span style={{ color: 'var(--text-secondary)' }}>
                            {currentPage * rowsPerPage + 1}-{Math.min((currentPage + 1) * rowsPerPage, filteredTableTasks.length)} of {filteredTableTasks.length}
                        </span>
                    </div>
                    <div style={{ display: 'flex', gap: 'var(--spacing-xs)' }}>
                        <button
                            className="btn btn-secondary"
                            onClick={() => setCurrentPage(0)}
                            disabled={currentPage === 0}
                        >
                            <ChevronsLeft size={16} />
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={() => setCurrentPage(p => Math.max(0, p - 1))}
                            disabled={currentPage === 0}
                        >
                            <ChevronLeft size={16} />
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={() => setCurrentPage(p => Math.min(totalPages - 1, p + 1))}
                            disabled={currentPage >= totalPages - 1}
                        >
                            <ChevronRight size={16} />
                        </button>
                        <button
                            className="btn btn-secondary"
                            onClick={() => setCurrentPage(totalPages - 1)}
                            disabled={currentPage >= totalPages - 1}
                        >
                            <ChevronsRight size={16} />
                        </button>
                    </div>
                </div>

                <div style={{ textAlign: 'right', marginTop: 'var(--spacing-md)' }}>
                    <button className="btn btn-primary" onClick={downloadCSV}>
                        <Download size={16} />
                        Download CSV
                    </button>
                </div>
            </div>
        </div>
    );
};

export default Delivery;